/**
 * Embeds follow the licence (owner request, 16 October 2026). The widget's own calls come from
 * widget.wapadesk.com, so the API cannot tell from Origin which website shows a page form, the
 * booking page or the chat in a frame. The frame names its embedding site in the `embed` query
 * parameter, and the API refuses sites the clinic is not licensed for. A link opened on its own
 * (Instagram bio, email signature) is not framed and works anywhere, as before.
 *
 * The parent comes from `location.ancestorOrigins` (Chrome, Safari; cannot be set by the page),
 * else from the referrer, which the loader sets to the origin on the frames it creates. When
 * neither is known nothing is sent: a clinic's site must never lose its form over a browser setting.
 */
export function embedderHost(
  win:
    | (Pick<Window, "self" | "top" | "location"> & { document: Pick<Document, "referrer"> })
    | undefined = typeof window === "undefined" ? undefined : window,
): string | undefined {
  if (!win) return undefined;
  let framed: boolean;
  try {
    framed = win.self !== win.top;
  } catch {
    framed = true; // reading `top` can throw in some sandboxes; that is a frame too
  }
  if (!framed) return undefined;
  const ancestors = (win.location as Location & { ancestorOrigins?: DOMStringList })
    .ancestorOrigins;
  const parent = ancestors && ancestors.length > 0 ? ancestors[0] : win.document.referrer;
  if (!parent) return undefined;
  try {
    const u = new URL(parent);
    return u.protocol === "https:" || u.protocol === "http:" ? u.hostname.toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}

/** The API path with `embed=<host>` added when the widget is framed by a known site. */
export function withEmbed(path: string, host = embedderHost()): string {
  if (!host) return path;
  return `${path}${path.includes("?") ? "&" : "?"}embed=${encodeURIComponent(host)}`;
}

/** The API refused this site: the clinic has not licensed the website that frames the widget. */
export function notLicensed(e: unknown): boolean {
  return (
    typeof e === "object" && e !== null && (e as { code?: unknown }).code === "embed_not_allowed"
  );
}
